import React from "react"
import { makeStyles } from "tss-react/mui"
import { Button, Typography } from "@mui/material"
import AwayIcon from "@mui/icons-material/NightsStay"

const useStyles = makeStyles()((theme) => ({
  // Full-screen scrim, matching KickedModal/ReconnectModal: unlike the warning
  // banner, this session really is over, so blocking the stale chat behind it
  // is correct.
  overlay: {
    position: "fixed",
    inset: 0,
    zIndex: theme.zIndex.modal + 10,
    backgroundColor: "rgba(15, 17, 21, 0.72)",
    backdropFilter: "blur(2px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  card: {
    width: 380,
    maxWidth: "90vw",
    backgroundColor: theme.palette.background.paper,
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: theme.shape.borderRadius * 2,
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(2),
    textAlign: "center",
    boxShadow: "0 12px 40px rgba(0,0,0,0.5)",
  },
  icon: { fontSize: 40, color: theme.palette.primary.main },
  title: {
    fontWeight: 700,
    fontSize: "1.05rem",
    color: theme.palette.text.primary,
  },
  detail: { fontSize: "0.85rem", color: theme.palette.text.secondary },
  actions: {
    display: "flex",
    marginTop: theme.spacing(1),
    width: "100%",
    justifyContent: "center",
    gap: theme.spacing(1),
  },
  rejoinButton: {
    backgroundColor: theme.palette.primary.main,
    color: "#000",
    fontWeight: 600,
    "&:hover": { backgroundColor: theme.palette.primary.dark },
  },
}))

/**
 * "Disconnected for inactivity" screen.
 *
 * Distinct from KickedModal: an admin kick ends the session and bounces to the
 * auth screen, whereas this keeps the user signed in — the camera and socket
 * are released to reclaim bandwidth, but Rejoin puts them straight back with no
 * re-login. Guests especially would otherwise lose their identity and come back
 * as a different person.
 *
 * Props:
 *   open - show it
 *   onRejoin - reconnect using the existing session
 *   onLogout - sign out properly (clears stored auth)
 */
const IdleKickedScreen = ({ open, onRejoin, onLogout }) => {
  const { classes } = useStyles()
  if (!open) return null

  return (
    <div className={classes.overlay}>
      <div className={classes.card}>
        <AwayIcon className={classes.icon} />
        <Typography className={classes.title}>
          Disconnected for inactivity
        </Typography>
        <Typography className={classes.detail}>
          You were away for a while, so we closed your connection and turned off
          your camera. You&apos;re still signed in.
        </Typography>
        <div className={classes.actions}>
          <Button
            className={classes.rejoinButton}
            variant="contained"
            onClick={onRejoin}
          >
            Rejoin
          </Button>
          <Button variant="text" color="inherit" onClick={onLogout}>
            Log out
          </Button>
        </div>
      </div>
    </div>
  )
}

export default IdleKickedScreen
